import React, { useMemo } from 'react';
import { Alert, Button } from 'react-bootstrap';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';

import FAIcon from '../../components/FAIcon/FAIcon';
import FilterableTable from '../../components/FilterableTable/FilterableTable';
import Page from '../../components/Page/Page';
import Title from '../../components/Title/Title';
import { companyAgreementConstants } from '../../constants/companyAgreementConstants';
import { platformFeatures } from '../../constants/platformConstants';
import { useReferenceData, useUserProfile } from '../../hooks/reduxHooks';
import {
	getDigitalOrders,
	getDigitalOrdersPlatformLegend
} from '../../services/digitalCodesService';
import { getUsersInBulk } from '../../services/usersService';
import { getActiveAgreements } from '../../utils/companyUtils';
import { getPermissions } from '../../utils/digitalCodesUtils';
import {
	getActivationStatus,
	getFiltersConfig,
	getTableFormat
} from './DigitalCodesManagement.helpers';
import { getAvailableFeaturePlatforms } from '../../utils/userUtils';
import { useCompanyAgreementsQuery } from '../../hooks/queryHooks';


const DigitalCodesManagement = () => {
	const userProfile = useUserProfile();
	const platforms = useReferenceData('platforms');
	const {
		canReview,
		canApprove,
		canViewCompanies,
		canCreateOrder,
		canViewCreditHoldOrders,
		canViewAllOrderDetails,
		canViewActivation, 
		isPublisher,
	} = useMemo(() => getPermissions(userProfile), [userProfile]);

	const ordersQuery = useQuery('getDigitalOrders', () => getDigitalOrders());
	const purposesQuery = useQuery(
		'getDigitalOrdersPlatformLegend', 
		() => getDigitalOrdersPlatformLegend(),
		{ enabled: canViewAllOrderDetails }
	);
	const agreementsQuery = useCompanyAgreementsQuery({
		enabled: isPublisher,
	});

	const submittedByNdidList = useMemo(() => {
		if (
			canViewCreditHoldOrders &&
			Array.isArray(ordersQuery.data?.data?.body)
		) {
			const ndids = ordersQuery.data.data.body
				.map(order => order.submitted_by_ndid)
				.filter(Boolean);
			return Array.from(new Set(ndids));
		}
		return [];
	}, [canViewCreditHoldOrders, ordersQuery.data]);

	const usersQuery = useQuery(
		['getUsersInBulk', ...submittedByNdidList],
		() =>
			getUsersInBulk(
				submittedByNdidList,
				['email_address']
			),
		{ enabled: !!submittedByNdidList.length,
			staleTime: Infinity },
	);
	const users = usersQuery.data?.data;

	const anyValidDigitalAgreement = () => {
		if (!agreementsQuery.data?.data) {
			return true;
		}

		let validDigitalAgreements = false;
		['HAC', 'BEE'].some(platformCode => {
			const digitalAgreements = getActiveAgreements(
				agreementsQuery.data?.data,
				companyAgreementConstants.TYPE.DIGITAL,
				platformCode,
			);
			if(Array.isArray(digitalAgreements) && digitalAgreements.length > 0) {
				validDigitalAgreements = true;
			}
		});
		return validDigitalAgreements;
	};

	// For the time being, this refers to platforms that have platform feature edit enabled
	const availablePlatforms = useMemo(() => {
		return getAvailableFeaturePlatforms(userProfile, platformFeatures.DIGITAL_CODES, 'edit');
	}, [userProfile]);

	const isLoading =
		ordersQuery.isLoading ||
		(canViewAllOrderDetails && purposesQuery.isLoading) ||		
		(isPublisher && agreementsQuery.isLoading) ||
		(canViewCreditHoldOrders && usersQuery.isLoading);
	const isSuccess =
		ordersQuery.isSuccess &&
		(!canViewAllOrderDetails || purposesQuery.isSuccess) &&
		(!canViewCreditHoldOrders || usersQuery.isSuccess);
	
	const tableFormat = useMemo(
		() =>
			(isSuccess &&
				getTableFormat({
					canCreateOrder,
					canViewCompanies,
					canViewAllOrderDetails,
					canViewCreditHoldOrders,
					availablePlatforms,
					users
				})) ||
			[],
		[
			isSuccess,
			canCreateOrder,
			canViewCompanies,
			canViewAllOrderDetails,
			availablePlatforms,
		],
	);
	const orders = 
		isSuccess
			? canViewAllOrderDetails
				? (
					ordersQuery.data.data.body.map((order) => {
						const item = {
							...order,
							game_code: order?.product?.game_code,
							game_name: order?.product?.game_name,
							code_activation: getActivationStatus(order),
						};
						return item;
					})
				) 
				: ordersQuery?.data?.data.body
			: []
	;

	const filtersConfig = useMemo(
		() =>
			orders && orders.length > 1 && canViewAllOrderDetails
				? userProfile && platforms &&
					getFiltersConfig({
						userProfile,
						isReviewer: canReview,
						isApprover: canApprove,
						isActivator: canViewActivation,
						purposesData: purposesQuery.data?.data,
						platforms,
					})
				: undefined,
		[userProfile, canReview, canApprove, purposesQuery.isSuccess, platforms, orders],
	);
	const searchableFields = 
		canViewCreditHoldOrders 
			? ['ebsSalesOrderNum', 'publisherPO']
			: ['game_code', 'game_name', 'ebsSalesOrderNum', 'publisherPO', 'orderID']
	;
	const searchableFieldPlaceHolder = canViewCreditHoldOrders 
		? 'Search by Publisher PO #'
		: 'Search by Product, Game Code or Publisher PO #'
	;

	const fault = (ordersQuery.isError && ordersQuery.error) ||
		(purposesQuery.isError && purposesQuery.error) ||
		null;
	
	return (
		<Page fault={fault} isLoading={isLoading}>
			{isPublisher && !anyValidDigitalAgreement() &&
				(agreementsQuery.isSuccess ? (
					<Alert variant="danger">
						The Global Digital agreement has expired. Please renew the agreement in the
						Nintendo Developer Portal to proceed.
					</Alert>
				) : (
					<Alert variant="danger">
						Agreements could not be retrieved and functions that require a valid
						agreement have been disabled. Reload or contact site support for assistance.
					</Alert>
				))}
			<Title
				title="Digital Code Orders"
				button={
					canCreateOrder &&
					Array.isArray(availablePlatforms) && availablePlatforms.length > 0 && (
						<Link to={'/orders/digital-codes/create'}>
							<Button variant="primary">
								<FAIcon name="plus" className="mr-1" />
								Create Digital Code Order
							</Button>
						</Link>
					)
				}
			/>
			{!isLoading && (
				<FilterableTable
					dataFormat={tableFormat}
					data={orders}
					searchableFieldPlaceHolder={searchableFieldPlaceHolder}
					searchableFields={searchableFields}
					filterProperties={filtersConfig}
					retainPageState
				/>
			)}
		</Page>
	);
};

export default DigitalCodesManagement;
